import { SORTING_ORDERS } from "../constants";

/**
 * @function getTempAsPerSelectedUnitSystem converts the incoming temperature value based on the logged-in user's
 * preferred unit that is stored in the local storage (Fahrenheit)
 * @param {number} temperature temperature value.
 * @returns {object} contains converted temperature value and unit.
 */
export const getTempAsPerSelectedUnitSystem = (temperature, isSubcool,failure, isSensorNotConnected, isNullValue) => {
  if(failure || isSensorNotConnected === '0' || isNullValue === null){
    return { temperature: "???", tempUnit: "" };
  }
  // if the incoming temperature value is not a number return --
  if (typeof temperature !== "number" || Number.isNaN(temperature)) {
    return { temperature: "--", tempUnit: "" };
  }

  // get user's unit preference from the local storage
  const { temperatureUnit } = JSON.parse(localStorage.getItem("user"));

  switch (temperatureUnit) {
    case "C":
      return {
        temperature: isSubcool ? (temperature * (5 / 9)).toFixed(2) : (((temperature - 32) * 5) / 9).toFixed(2),
        tempUnit: "\u00b0C",
      };
    case "F":
      return { temperature: temperature.toFixed(2), tempUnit: "\u00b0F" };
    case "K":
      return {
        temperature: (((temperature - 32) * 5) / 9 + 273.15).toFixed(2),
        tempUnit: "K",
      };
    default:
      return { temperature: temperature.toFixed(2), tempUnit: "\u00b0F" };
  }
};

/**
 * @function getVolumeAsPerSelectedUnitSystem converts the incoming volume value based on the logged-in user's
 * preferred unit that is stored in the local storage
 * @param {number} volume volume value.
 * @returns {object} contains converted volume value and unit.
 */
export const getVolumeAsPerSelectedUnitSystem = (volume) => {
  // if the incoming volume value is not a number return --
  if (typeof volume !== "number" || Number.isNaN(volume)) {
    return { volume: "--", volumeUnit: "" };
  }

  // get user's unit preference from the local storage
  const { volumeUnit } = JSON.parse(localStorage.getItem("user"));

  switch (volumeUnit) {
    case "gal":
      return { volume, volumeUnit: "gal" };
    case "L":
      return { volume: Math.round(volume * 3.78), volumeUnit: "L" };
    case "lb":
      return { volume: Math.round(volume * 8.6), volumeUnit: "lb" };
    case "kg":
      return { volume: Math.round(volume * 8.6 * 0.45), volumeUnit: "kg" };
    default:
      return { volume, volumeUnit: "gal" };
  }
};

/**
 * @function getPressureAsPerSelectedUnitSystem converts the incoming temperature value based on the logged-in user's
 * preferred unit that is stored in the local storage
 * @param {number} temperature temperature value.
 * @returns {object} contains converted temperature value and unit.
 */
export const getPressureAsPerSelectedUnitSystem = (pressure) => {
  // if the incoming temperature value is not a number return --

  if (typeof pressure !== "number" || Number.isNaN(pressure)) {
    return { pressure: "--", pressureUnit: "" };
  }

  // get user's unit preference from the local storage
  const { pressureUnit } = JSON.parse(localStorage.getItem("user"));

  switch (pressureUnit) {
    case "psig":
      return { pressure: pressure.toFixed(2), pressureUnit: "psig" };
    case "bar":
      return {
        pressure: (pressure * 0.069).toFixed(2),
        pressureUnit: "bar",
      };
    case "MPa":
      return {
        pressure: (pressure * 0.0069).toFixed(2),
        pressureUnit: "MPa",
      };
    default:
      return { pressure, pressureUnit: "psig" };
  }
};

export const getStringFromHexadecimalToBinary = (val, bitAddress) => {
  // converting Hex value to binary 
  let tempVal = Array.from("0".repeat(18));
  tempVal = parseInt(val, 16).toString(2);
  tempVal = tempVal.split("").reverse().join("");
  return bitAddress >= 0 ? tempVal[bitAddress] : tempVal;
};

export const getStringFromDecimalToBinary = (val, bitAddress) => {
    // defining 0 to 17 bits as 0 value;
    let tempVal = Array.from("0".repeat(18));
    // converting hexadecimal to binary and reversing the array;
    let convertedBinary = parseInt(val).toString(2).split("").reverse();
    // re-assigning the converted values into main array
    tempVal.splice.apply(tempVal, [0, convertedBinary.length].concat(convertedBinary));
    return bitAddress >= 0 ? tempVal.at(bitAddress) : tempVal;
};

/**
 * @function getEnableStatus converts the register value into Status Text
 * @param {string|number} value 
 * @param {string} [enabledText='Enabled'] 
 * @param {string} [disabledText='Disabled'] 
 * @returns {string} status value [Enabled, Disabled, -]
 */
export const getEnableStatus = (value, enabledText = 'Enabled', disabledText = 'Disabled') => {
  const num = parseInt(value, 10);
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(num)) {
    return '-';
  }
  return (num > 0) ? enabledText : disabledText;
}

export const getBinaryStringFromDecimal = (val, totalBits) => {
	const tempVal = Array.from('0'.repeat(totalBits));
	const convertedBinary = parseInt(val).toString(2).split('').reverse();
	tempVal.splice.apply(tempVal, [0, convertedBinary.length].concat(convertedBinary));
	return tempVal;
};

export const findCompressorNumberByValue = (indicator) => {
	const evcIdentificationNumbers = {
		"Bottom Unit #1": [32, 36, 40, 44],
		"Bottom Unit #2": [33, 37, 41, 45],
		"Side Unit #3": [34, 38, 42, 46],
		"Side Unit #4": [35, 39, 43, 47],
	};
	return Object.keys(evcIdentificationNumbers).find((key) => evcIdentificationNumbers[key].includes(indicator));
};

/**
 * Sort an object by key or by a nested value
 *
 * @param {Object} obj - Source object
 * @param {Function} selector - Function that returns value to sort by
 * @param {"asc"|"desc"} order - Sort order
 * @returns {Object} Sorted object
 */
export function sortObject(obj, selector = ([key]) => key, order = SORTING_ORDERS.ASC) {
  const factor = order === SORTING_ORDERS.DESC ? -1 : 1;

  return Object.fromEntries(
    Object.entries(obj).sort((a, b) => {
      const valA = selector(a);
      const valB = selector(b);

      if (valA > valB) return 1 * factor;
      if (valA < valB) return -1 * factor;
      return 0;
    })
  );
}

export function toFixed2(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '--';
}


/**
 * Returns data interval based on the difference
 * in days between two date-time values.
 *
 * @param {string | Date} startDateTime - Start date-time (ISO string or Date)
 * @param {string | Date} endDateTime - End date-time (ISO string or Date)
 * @returns {string} Interval description (e.g. "1 minute", "10 seconds")
 */
export function getDataInterval(startDateTime, endDateTime) {
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);

  const msInDay = 1000 * 60 * 60 * 24;
  const diffMs = end - start;

  const differenceInDays = Math.floor(diffMs / msInDay);
  let duration;
  if (differenceInDays <= 1) {
    duration = '10 seconds';
  } else if (differenceInDays <= 2) {
    duration = '20 seconds';
  } else if (differenceInDays <= 7) {
    duration = '1 minute';
  } else if (differenceInDays >= 7) {
    duration = '10 minutes';
  } else {
    duration = '10 seconds'
  }

  return duration
}